import { useFeatureFlags } from 'hook/useFeatureFlag';
import { TraderEventType } from '@utils/eventsConstant';
import { footerNew } from '@utils/lists';
import { getNativeUtils, redirectToPage } from '@utils/nativeUtils';
import { ROUTE_NAMES } from '@utils/routes';
import { FeatureFlag } from '@utils/featureFlag.enum';
import { FooterMenuName } from 'constants/types';
import { useGlobalContext } from 'contexts';
import { useRouter } from 'next/router';
import { memo, useMemo } from 'react';
import Image from 'next/image';
import { getStorageItem, getWin, setStorageItem } from '@utils/storage';
import { STORAGE_KEYS } from '@utils/keys';
import BusinessProfileDialog from '../dialogs/business-profile-dialog/BusinessProfileDialog';
import useEvents from 'hook/useEvents';
import { checkForSMSPull, isNativeFeatureAvailible } from '@utils/Utils';
import { FEATURE_RELEASE_VERSION } from '@utils/types/commontypes';

function Footer() {
  const { setShowBalanceClicked, ntcEnabled } = useGlobalContext();
  const sms_permission = getStorageItem(STORAGE_KEYS.SMS_PERMISSION);
  const smsCounter =
    Number(getStorageItem(STORAGE_KEYS.SMS_CONSENT_COUNTER)) || 0;
  const { smsLimitSet } = useGlobalContext();

  const selectedFooter = getStorageItem(STORAGE_KEYS.SELECTED_FOOTER) || 'Home';

  const isStaffView = getStorageItem('isStaffView');
  const { triggerEvent } = useEvents();

  const router = useRouter();
  const treatments = useFeatureFlags([
    FeatureFlag.ScanPay,
    FeatureFlag.HomeIconFooter,
    FeatureFlag.TransactionHpFooter,
    FeatureFlag.LoansHpFooter,
    FeatureFlag.PartiesHpFooter,
    FeatureFlag.CommerceHpFooter,
    FeatureFlag.PartiesTierBadge,
    FeatureFlag.NFCCommerce,
    FeatureFlag.view_ninja_one,
    FeatureFlag.Mandi_News,
    FeatureFlag.NTCLoadMatirxLazily,
    FeatureFlag.sms_home_page,
  ]);

  const showNTC =
    ntcEnabled || getStorageItem(STORAGE_KEYS.NTC_ENABLED) || false;
  const falsyValue = false;

  const footerClassName =
    isStaffView && showNTC
      ? 'tw-fixed tw-bottom-0 tw-z-10 tw-grid tw-h-16 tw-w-full tw-grid-cols-3 tw-items-center tw-gap-[10px] tw-border-2 tw-border-solid tw-bg-white'
      : isStaffView
      ? 'tw-fixed tw-bottom-0 tw-z-10 tw-grid tw-h-16 tw-w-full tw-grid-cols-2 tw-items-center tw-gap-[10px] tw-border-2 tw-border-solid tw-bg-white'
      : showNTC
      ? 'tw-fixed tw-bottom-0 tw-z-10 tw-grid tw-h-20 tw-w-full tw-grid-cols-3 tw-items-center tw-gap-[10px] tw-border-2 tw-border-solid tw-bg-white tw-pb-[20px] tw-pt-4'
      : 'tw-fixed tw-bottom-0 tw-z-10 tw-grid tw-h-16 tw-w-full tw-grid-cols-2 tw-items-center tw-gap-[10px] tw-border-2 tw-border-solid tw-bg-white';

  const footerlist = useMemo(() => {
    return footerNew().filter((item) => {
      const { key, staffAccess, menuName } = item;
      let hasAccess = true;
      let hasStaffAccess = true;
      if (isStaffView) {
        hasStaffAccess = Boolean(staffAccess);
      }
      if (key) {
        hasAccess = treatments[key].treatment === 'on';
      }
      if (menuName === 'NTC') {
        hasAccess = showNTC;
      }

      return hasAccess && hasStaffAccess;
    });
  }, [isStaffView, treatments, showNTC]);

  const footerHandler = async (url: string, menuName: FooterMenuName) => {
    const androidVersion = +(
      getStorageItem(STORAGE_KEYS.APP_BUILD_VERSION) || 0
    );
    setStorageItem(STORAGE_KEYS.SELECTED_FOOTER, menuName);
    setShowBalanceClicked(false);
    if (menuName === 'Home') {
      router.push(ROUTE_NAMES.DASHBOARD);
      return;
    } else if (menuName === 'Transactions') {
      triggerEvent(TraderEventType.Generic_event, {
        eventName: 'Payment_statement_view',
      });
      router.push(ROUTE_NAMES.TRANSACTION_HISTORY);
      return;
    }

    if (menuName === 'NTC') {
      if (isNativeFeatureAvailible(FEATURE_RELEASE_VERSION.NINJA_ONE)) {
        if (
          androidVersion > 187 &&
          smsLimitSet &&
          treatments[FeatureFlag.sms_home_page].treatment === 'on'
        ) {
          if (
            !sms_permission &&
            smsCounter < 2 &&
            // Manually blocking the route to consent-sms as finbox is not present in android build
            falsyValue
          ) {
            router.push({
              pathname: ROUTE_NAMES.CONSENT_SMS,
              query: { app: 'NTC' },
            });
          } else if (sms_permission) {
            checkForSMSPull();
            const timeStamp = Date.now();
            getNativeUtils()?.openWebview(
              `${process.env.NEXT_PUBLIC_NINJA_ONE_URL}/buyer/dashboard?ntcInitialTimeStamp=${timeStamp}`,
              'NinjaOneMfe',
              {
                ...getWin().localStorage,
                [STORAGE_KEYS.NTC_MATRIX_LAZYLOAD]:
                  treatments[FeatureFlag.NTCLoadMatirxLazily].treatment ===
                  'on',
              }
            );
          } else {
            const timeStamp = Date.now();
            getNativeUtils()?.openWebview(
              `${process.env.NEXT_PUBLIC_NINJA_ONE_URL}/buyer/dashboard?ntcInitialTimeStamp=${timeStamp}`,
              'NinjaOneMfe',
              {
                ...getWin().localStorage,
                [STORAGE_KEYS.NTC_MATRIX_LAZYLOAD]:
                  treatments[FeatureFlag.NTCLoadMatirxLazily].treatment ===
                  'on',
              }
            );
          }
        } else {
          const timeStamp = Date.now();
          getNativeUtils()?.openWebview(
            `${process.env.NEXT_PUBLIC_NINJA_ONE_URL}/buyer/dashboard?ntcInitialTimeStamp=${timeStamp}`,
            'NinjaOneMfe',
            {
              ...getWin().localStorage,
              [STORAGE_KEYS.NTC_MATRIX_LAZYLOAD]:
                treatments[FeatureFlag.NTCLoadMatirxLazily].treatment === 'on',
            }
          );
        }
        return;
      }
    }
    redirectToPage(url, menuName);
  };

  return (
    <>
      <div
        className={footerClassName}
        style={{ boxShadow: '0px -3px 16px 0px #0000001A' }}
      >
        {footerlist.map(({ menuName, icon, activeIcon, url, name }) => (
          <div
            key={menuName}
            onClick={() => {
              footerHandler(url, menuName);
            }}
          >
            <div className="tw-flex tw-items-center tw-justify-center">
              <Image
                alt=""
                className=""
                height={24}
                src={selectedFooter === menuName ? activeIcon : icon}
                width={24}
              />
            </div>
            <span
              className={`bodyLabel tw-text-lightBlackText' tw-mt-1 tw-flex tw-items-center tw-justify-center tw-text-center ${
                selectedFooter === menuName ? 'tw-text-ninja-primary-600' : ''
              }`}
            >
              {name}
            </span>
          </div>
        ))}
      </div>

      <BusinessProfileDialog />
    </>
  );
}

export default memo(Footer);
